import { Img } from "@/components/site/Img";
import { MessageCircle, Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PHONE_HREF, PHONE_DISPLAY, SMS_DRAFTS } from "@/lib/contact";
import { useSmsHref } from "@/components/site/ContactButtons";
import { track } from "@/lib/track";

const SPECS = [
  ["Size", "7\u00BD ft tall \u00B7 7 ft wide"],
  ["Timeline", "3 weeks to make \u00B7 4 days to install"],
  ["On the blocks", "Lions, horse heads, planters, Buddha, foo dogs, or angels"],
  ["Toppers", "Ball, flower, pineapple, or a 3rd tier"],
  ["Finish", "Fully customizable \u00B7 any color"],
];

export default function SignatureMajestic() {
  const majesticSms = useSmsHref(SMS_DRAFTS.majestic("I'd like to talk about building one"));
  return (
    <section
      id="majestic"
      data-testid="signature-majestic-section"
      className="bg-[#1F1E1C] text-[#F9F6F0] border-t border-[#3a3733]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="zoom-container relative aspect-[4/5] lg:aspect-[4/4.5] overflow-hidden bg-[#2C2A28]">
            <Img
              src="/images/majestic-new-install.jpg"
              alt="A freshly installed Majestic fountain in bright white with planter blocks and a flower topper, Villa Rica, GA"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[#C8B387] text-[#1F1E1C] text-[10px] tracking-[0.28em] uppercase">
              Signature Fountain
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8B387] mb-4">
              The one he&rsquo;s known for
            </div>
            <h2
              data-testid="signature-majestic-heading"
              className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5"
            >
              The Majestic.
            </h2>
            <p className="text-[#C9C4BA] text-base md:text-lg leading-relaxed mb-8 max-w-xl">
              Chris&rsquo;s signature fountain and his most prominent piece: a grand
              three-tier centerpiece built from scratch and installed at your home.
              Every one is made to order, so no two are alike.
            </p>

            <dl className="space-y-3 mb-8">
              {SPECS.map(([label, value]) => (
                <div key={label} className="flex gap-4 items-baseline border-b border-white/10 pb-3">
                  <dt className="text-[10px] tracking-[0.24em] uppercase text-[#7A6547] w-28 flex-shrink-0">
                    {label}
                  </dt>
                  <dd className="text-sm md:text-base text-[#EDE9DF]">{value}</dd>
                </div>
              ))}
            </dl>

            <div data-testid="signature-majestic-price" className="mb-8">
              <span className="font-serif text-3xl md:text-4xl text-[#C8B387]">$4,500</span>
              <span className="text-[#6F6A60] text-sm ml-3">+ delivery from the shop</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={PHONE_HREF}
                onClick={() => track("call_click", "The Majestic (signature)")}
                data-testid="signature-majestic-call"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#7A6547] text-[#F9F6F0] text-[11px] tracking-[0.2em] uppercase hover:bg-[#5E4D35] transition-colors"
              >
                <Phone size={13} strokeWidth={1.5} />
                Start Yours &mdash; {PHONE_DISPLAY}
              </a>
              <a
                href={majesticSms}
                onClick={() => track("text_click", "The Majestic (signature)")}
                data-testid="signature-majestic-messenger"
                aria-label="Text Chris about a Majestic"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-[#C8B387] text-[#C8B387] text-[11px] tracking-[0.2em] uppercase hover:bg-[#C8B387] hover:text-[#1F1E1C] transition-colors"
              >
                <MessageCircle size={13} strokeWidth={1.5} />
                Ask About One
              </a>
            </div>

            <Link
              to="/majestic"
              data-testid="signature-majestic-gallery-link"
              className="inline-flex items-center gap-2 mt-6 text-[#7A6547] hover:text-[#C8B387] text-[11px] tracking-[0.22em] uppercase transition-colors"
            >
              All options, photos &amp; video
              <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
